import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import WhyChooseUs from './components/WhyChooseUs';
import PropertyListings from './components/PropertyListings';
import MediaCoverage from './components/MediaCoverage';
import TopDevelopers from './components/TopDevelopers';
import TrendingLocalities from './components/TrendingLocalities';
import PossessionTimeline from './components/PossessionTimeline';
import Reviews from './components/Reviews';
import SignUpBanner from './components/SignUpBanner';
import StatsSection from './components/StatsSection';
import LegalConsultation from './components/LegalConsultation';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import AuthModal from './components/AuthModal';
import { prefetchHomeData } from './services/api';

// Public Pages Lazy Loaded
const BuilderPage = lazy(() => import('./pages/BuilderPage'));
const LocalityPage = lazy(() => import('./pages/LocalityPage'));
const CityPage = lazy(() => import('./pages/CityPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const PublicLegalPage = lazy(() => import('./pages/LegalPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));

/* ── Admin Lazy Loaded ── */
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/layout/AdminLayout'));
const ProtectedRoute = lazy(() => import('./admin/components/ProtectedRoute'));
const Dashboard = lazy(() => import('./admin/pages/Dashboard'));
const PropertiesPage = lazy(() => import('./admin/pages/PropertiesPage'));
const UsersPage = lazy(() => import('./admin/pages/UsersPage'));

const LegalPage = lazy(() => import('./admin/pages/LegalPage'));
const AboutPage = lazy(() => import('./admin/pages/AboutPage'));
const SettingsPage = lazy(() => import('./admin/pages/SettingsPage'));
const AdminSettingsPage = lazy(() => import('./admin/pages/AdminSettingsPage'));
const AdminContactPage = lazy(() => import('./admin/pages/ContactPage'));
const CallbackRequestsPage = lazy(() => import('./admin/pages/CallbackRequestsPage'));
const FooterSettingsManager = lazy(() => import('./admin/pages/FooterSettingsManager'));

/* Scroll to top on every route change */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AuthRouteEnforcer = ({ isAdminSubdomain, user, setAuthModal }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const legalOnlyRoutes = ['/privacy-policy', '/terms-conditions'];
    if (legalOnlyRoutes.includes(pathname)) {
      setAuthModal((prev) => (prev.open ? { ...prev, open: false } : prev));
      return;
    }

    const hasToken = !!localStorage.getItem('userToken');
    if (isAdminSubdomain || user || hasToken) return;

    setAuthModal((prev) => (prev.open ? prev : { open: true, mode: 'login' }));
  }, [isAdminSubdomain, user, pathname, setAuthModal]);

  return null;
};

/* Home Page — all the homepage sections */
const HomePage = ({ user, onLoginClick, onSignUpClick, onLogout }) => {
  const [selectedCity, setSelectedCity] = useState('Mumbai');
  
  // Create a context or just let the components fetch via React Query or SWR?
  // Currently they are probably fetching on mount inside each component.

  return (
    <div className="min-h-screen bg-white">
      <Navbar 
        user={user} 
        onLoginClick={onLoginClick} 
        onSignUpClick={onSignUpClick} 
        onLogout={onLogout}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
      />
      <Hero selectedCity={selectedCity} />
      <PropertyListings user={user} onLoginClick={onLoginClick} selectedCity={selectedCity} />
      <WhyChooseUs/>
      <MediaCoverage />
      <TopDevelopers selectedCity={selectedCity} />
      <TrendingLocalities selectedCity={selectedCity} />
      <PossessionTimeline />
      <Reviews />
      {!user && <SignUpBanner onSignUpClick={onSignUpClick} />}
      <StatsSection />
      <LegalConsultation />
      <Footer />
      <WhatsAppButton user={user} onLoginClick={onLoginClick} />
    </div>
  );
};

function App() {
  const [authModal, setAuthModal] = useState({ open: false, mode: 'login' });
  const [user, setUser] = useState(null);

  // Detect if accessing from admin subdomain
  const isAdminSubdomain =
    window.location.hostname === 'admin.metrobrix.com' ||
    window.location.hostname === 'admin.localhost'; // for local testing

  // Restore user from localStorage on mount
  useEffect(() => {
    if (!isAdminSubdomain) {
      prefetchHomeData('Mumbai').catch(() => {});
    }

    const legalOnlyRoutes = ['/privacy-policy', '/terms-conditions'];
    const path = window.location.pathname;
    const token = localStorage.getItem('userToken');
    const saved = localStorage.getItem('userData');
    if (saved) {
      try { setUser(JSON.parse(saved)); } catch { /* ignore */ }
    }

    // Show login popup whenever visiting the site if not logged in and not admin
    if (!isAdminSubdomain && !token && !legalOnlyRoutes.includes(path)) {
      const timer = setTimeout(() => {
        setAuthModal({ open: true, mode: 'login' });
      }, 3000); // 3 seconds delay for data loading and user experience
      return () => clearTimeout(timer);
    }
  }, [isAdminSubdomain]);

  useEffect(() => {
    if (user && authModal.open) {
      setAuthModal({ open: false, mode: 'login' });
    }
  }, [user, authModal.open]);

  const handleAuth = (userData) => {
    setUser(userData);
    setAuthModal({ open: false, mode: 'login' });
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    setAuthModal({ open: true, mode: 'login' });
  };

  const openLogin = () => setAuthModal({ open: true, mode: 'login' });
  const openSignUp = () => setAuthModal({ open: true, mode: 'register' });
  const closeAuth = () => setAuthModal({ open: false, mode: 'login' });

  const authProps = { user, onLoginClick: openLogin, onSignUpClick: openSignUp, onLogout: handleLogout, onUserUpdate: handleAuth };

  return (
    <Router>
      <ScrollToTop />
      <AuthRouteEnforcer isAdminSubdomain={isAdminSubdomain} user={user} setAuthModal={setAuthModal} />
      {/* Auth Modal (global - only for public site) */}
      {!isAdminSubdomain && (
        <AuthModal
          isOpen={authModal.open}
          onClose={closeAuth}
          onAuth={handleAuth}
          initialMode={authModal.mode}
          isMandatory={!user}
        />
      )}

      <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-lg font-semibold">Loading...</div>}>
      <Routes>
        {/* Public Routes — hidden on admin subdomain */}
        {!isAdminSubdomain && (
          <>
            <Route path="/" element={<HomePage {...authProps} />} />
                        <Route path="/mumbai-real-estate" element={<HomePage {...authProps} />} />
            <Route path="/search" element={<SearchResultsPage {...authProps} />} />
            <Route path="/builder/:slug" element={<BuilderPage {...authProps} />} />
            <Route path="/in/:city" element={<CityPage {...authProps} />} />
            <Route path="/in/:city/:locality" element={<LocalityPage {...authProps} />} />
            <Route path="/projects/:slug" element={<ProjectDetailPage {...authProps} />} />
            <Route path="/profile" element={<UserProfile {...authProps} />} />
            <Route path="/contact" element={<ContactPage {...authProps} />} />
            <Route path="/about" element={<AboutUsPage {...authProps} />} />
            <Route path="/privacy-policy" element={<PublicLegalPage {...authProps} legalType="privacy" />} />
            <Route path="/terms-conditions" element={<PublicLegalPage {...authProps} legalType="terms" />} />
            <Route path="/contact-us" element={<ContactPage {...authProps} />} />
            <Route path="/calculators" element={<CalculatorPage {...authProps} />} />
          </>
        )}

        {/* On admin subdomain: redirect root/login → /admin equivalents */}
        {isAdminSubdomain && (
          <>
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          </>
        )}

        {/* Admin Routes — always available at /admin/* */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="legal" element={<LegalPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="footer-settings" element={<FooterSettingsManager />} />
          <Route path="contact" element={<AdminContactPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="admin-settings" element={<AdminSettingsPage />} />
          <Route path="callback-requests" element={<CallbackRequestsPage />} />
        </Route>
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
